import { useCallback, useMemo } from 'react';
import { Navigate, useNavigate } from '@tanstack/react-router';

import { useGetPageProducts } from '@/api/hooks/queries/use-get-product-catalog-query';
import { Button } from '@/components/ui/button.tsx';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card.tsx';
import { useProductSelectionStep } from '@/hooks/use-product-selection-step';
import { useSessionSave } from '@/hooks/use-session-save';
import {
  addProductIds,
  removeProductIds,
  useSessionStore,
} from '@/hooks/use-session-store/use-session-store';
import { LOGGER_ACTIONS, logger } from '@/lib/logger';
import { PRODUCT_STEPS } from '@/lib/products.ts';
import { getActionablePlanFamilyCarrierTypes } from '@/types/insurance-builder.type.ts';
import { CATEGORY_TYPE } from '@/types/product.type.ts';
import ProductStepper from '@/views/layouts/ProductStepper.tsx';
import ProductCheckboxGroup from '@/views/products/components/ProductCheckboxGroup.tsx';
import { ProductSelectionSubmitFeedback } from '@/views/products/components/ProductSelectionSubmitFeedback.tsx';
import SkipButton from '@/views/products/components/SkipButton.tsx';

export default function SelectAccessibility() {
  const { saveSession } = useSessionSave();
  const { insuranceBuilder, selectedProductIds } = useSessionStore();

  const { products, isSuccess } = useGetPageProducts({
    categoryType: CATEGORY_TYPE.ACCESSIBILITY,
    patientCarrierTypes: getActionablePlanFamilyCarrierTypes(insuranceBuilder),
  });

  const updateAccessibility = useCallback(
    (newSelectedIds: string[]) => {
      const unselected = products.filter(
        (product) => !newSelectedIds.some((id) => id === product.uuid)
      );
      removeProductIds(unselected.map((p) => p.uuid));
      addProductIds(newSelectedIds);
    },
    [products]
  );

  const numProducts = useMemo(() => {
    return products.filter((p) => selectedProductIds.includes(p.uuid)).length;
  }, [products, selectedProductIds]);

  const navigate = useNavigate();
  const { nextStep: nextPath } = useProductSelectionStep();
  const skipSelection = () => {
    removeProductIds(products.map((p) => p.uuid));
    // Auto-save session (non-blocking)
    saveSession();

    logger.action(LOGGER_ACTIONS.INTAKE.PRODUCT_SELECTION_SKIPPED, {
      product_category: CATEGORY_TYPE.ACCESSIBILITY,
    });
    navigate({ to: nextPath });
  };

  const handleAddToOrder = () => {
    // Auto-save session (non-blocking)
    saveSession();
    navigate({ to: nextPath });
  };

  const noEligibleProducts = isSuccess && products.length === 0;
  if (noEligibleProducts) {
    return <Navigate to={nextPath} replace />;
  }

  return (
    <section>
      <ProductStepper steps={PRODUCT_STEPS} />
      <div className="space-y-6">
        <Card spacing="sm">
          <CardHeader>
            <CardTitle>Choose home accessibility aids</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <ProductCheckboxGroup
              allProducts={products}
              selectedProductIds={selectedProductIds}
              onSelectedChange={updateAccessibility}
            />
          </CardContent>
        </Card>
        <div className="grid grid-cols-[1fr_3fr] gap-3">
          <SkipButton categoryType={CATEGORY_TYPE.ACCESSIBILITY} onSkip={skipSelection} />
          <Button onClick={handleAddToOrder}>Add to order ({numProducts})</Button>
        </div>
        <ProductSelectionSubmitFeedback currentCategory={CATEGORY_TYPE.ACCESSIBILITY} />
      </div>
    </section>
  );
}
